import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MACRO_HUMAN = { protein: 'proteína', carbs: 'carbos', fats: 'grasa' };

// Primer macro con déficit real (scaled < target y fuera de ±10%) según el
// macro_diagnostics guardado en la receta. Misma regla que RecipeLibraryModal.
export const detectRecipeDeficit = (diag) => {
  if (!diag || typeof diag !== 'object') return null;
  // Ticket 280983: se elige el macro con MAYOR deficit relativo, no el primero en
  // un orden fijo (antes proteina > carbos > grasa tapaba deficits mas grandes).
  let best = null;
  for (const k of ['protein', 'carbs', 'fats']) {
    const d = diag[k];
    if (d && d.within_tolerance === false && Number(d.scaled) < Number(d.target)) {
      const gap = Number(d.target) - Number(d.scaled);
      const rel = Number(d.target) > 0 ? gap / Number(d.target) : 0;
      if (!best || rel > best.rel) best = { macro: k, deficit_g: Math.max(0.1, gap), rel };
    }
  }
  return best ? { macro: best.macro, deficit_g: best.deficit_g } : null;
};

// Ticket 280983: si las calorias YA superan la meta (fuera de tolerancia), sumar un
// alimento solo las empeora: no se ofrece agregar.
export const caloriesOverTarget = (diag) => {
  const c = diag && typeof diag === 'object' ? diag.calories : null;
  return !!c && c.within_tolerance === false && Number(c.scaled) > Number(c.target);
};

// Ticket 280983 · macros que ya estan POR ENCIMA de la meta (fuera de tolerancia).
// El aviso solo ofrecia sumar alimentos; ahora tambien informa lo que sobra para que
// quede claro que sumar un alimento puede agravar ese exceso.
export const detectRecipeSurplus = (diag) => {
  if (!diag || typeof diag !== 'object') return [];
  const out = [];
  for (const k of ['protein', 'carbs', 'fats']) {
    const d = diag[k];
    if (d && d.within_tolerance === false && Number(d.scaled) > Number(d.target)) {
      out.push({ macro: k, surplus_g: Number(d.scaled) - Number(d.target) });
    }
  }
  return out;
};

const _dismissKey = (recipe) => `close-macro-dismissed:${recipe.id}:${recipe.replaced_at || ''}`;
const _isDismissed = (recipe) => {
  try { return localStorage.getItem(_dismissKey(recipe)) === '1'; } catch { return false; }
};

// 2026-10-04 · Aviso "cerrar déficit" en la TARJETA de la receta (Fit Evolution).
// Antes solo existía dentro del modal de la biblioteca, que se cierra al
// reemplazar: la tarjeta del plan nunca avisaba que la receta quedaba corta.
// 2026-10-13 · `adminEmail`: misma tarjeta en el panel de administración
// (UserPlanViewer), actuando sobre el plan de esa clienta con el token admin.
// Por que no se pudo cerrar el deficit subiendo un ingrediente que la receta ya tiene.
const BOOST_REASON_TEXT = {
  portion_cap: (m) => `No se pudo cubrir la ${m} subiendo un ingrediente de la receta sin pasarse de una porcion razonable. Elige un alimento para agregar.`,
  calories_overshoot: (m) => `Subir un ingrediente para cubrir la ${m} haria pasar las calorias de la meta. Elige un alimento para agregar.`,
  protein_overshoot: () => 'Subir un ingrediente haria pasar la proteina de la meta. Elige un alimento para agregar.',
  carbs_overshoot: () => 'Subir un ingrediente haria pasar los carbohidratos de la meta. Elige un alimento para agregar.',
  fats_overshoot: () => 'Subir un ingrediente haria pasar la grasa de la meta. Elige un alimento para agregar.',
  no_source_ingredient: (m) => `La receta no tiene un ingrediente que aporte ${m} para subir. Elige un alimento para agregar.`,
};

export default function CloseMacroCardWarning({ recipe, batchId, onUpdated, adminEmail }) {
  const deficit = detectRecipeDeficit(recipe?.macro_diagnostics);
  const surplus = detectRecipeSurplus(recipe?.macro_diagnostics);
  const calOver = caloriesOverTarget(recipe?.macro_diagnostics);
  const [dismissed, setDismissed] = useState(() => (recipe ? _isDismissed(recipe) : false));
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chosen, setChosen] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // 2026-10-23 · propuesta de subir el ingrediente que la receta ya tiene (ej. mas pollo)
  const [boost, setBoost] = useState(null);
  const [pickFood, setPickFood] = useState(false);
  // Motivo por el que no se pudo subir el ingrediente existente (se muestra sobre la lista).
  const [boostReason, setBoostReason] = useState('');

  if (!recipe || !batchId || !deficit || dismissed) return null;
  const macroName = MACRO_HUMAN[deficit.macro] || deficit.macro;

  const authHeaders = () => {
    const token = localStorage.getItem(adminEmail ? 'deisycfit_admin_token' : 'deisycfit_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  const closeMacroBase = adminEmail
    ? `${API}/admin/user-plans/${encodeURIComponent(adminEmail)}/recipe-close-macro`
    : `${API}/user/recipe-close-macro`;

  const loadFoods = async () => {
    setPickFood(true);
    if (options.length) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${closeMacroBase}/foods`, {
        headers: authHeaders(),
        params: { macro: deficit.macro, limit: 500 },
      });
      // Solo se aceptan alimentos de la respuesta que corresponde al macro pedido.
      setOptions(data?.macro === deficit.macro ? (data?.options || []) : []);
    } catch (e) {
      toast.error('No se pudo cargar la lista de alimentos');
    } finally {
      setLoading(false);
    }
  };

  const boostBody = (apply) => ({
    macro: deficit.macro, deficit_g: deficit.deficit_g,
    batch_id: batchId, recipe_id: recipe.id, apply,
  });

  // Primero se intenta subir el ingrediente existente; solo si no es viable
  // (calorias/otro macro se pasarian de la meta, o no hay fuente) se ofrece
  // la lista para agregar un alimento nuevo.
  const openPicker = async () => {
    setOpen(true);
    setLoading(true);
    let plan = null;
    let reason = '';
    try {
      const { data } = await axios.post(`${closeMacroBase}/boost`, boostBody(false), { headers: authHeaders() });
      plan = data?.viable ? data : null;
      reason = data?.viable ? '' : (data?.reason || '');
    } catch (e) { plan = null; }
    setBoost(plan);
    setBoostReason(reason);
    setLoading(false);
    if (!plan) await loadFoods();
  };

  const submitBoost = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${closeMacroBase}/boost`, boostBody(true), { headers: authHeaders() });
      toast.success(`Se subió ${data.name} de ${data.from_g} g a ${data.to_g} g para cerrar el déficit de ${macroName}`);
      setOpen(false);
      onUpdated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo subir el ingrediente');
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async () => {
    if (!chosen) { toast.error('Elegí un alimento primero'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${closeMacroBase}/add-ingredient`, {
        scope: 'fit_evolution',
        batch_id: batchId,
        recipe_id: recipe.id,
        macro: deficit.macro,
        deficit_g: deficit.deficit_g,
        food_name: chosen,
      }, { headers: authHeaders() });
      toast.success(`Se agregaron ${data.added_grams}g de ${chosen} para cerrar el déficit de ${macroName}`);
      setOpen(false);
      onUpdated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo sumar el alimento');
    } finally {
      setSubmitting(false);
    }
  };

  const dismiss = () => {
    try { localStorage.setItem(_dismissKey(recipe), '1'); } catch { /* sin storage: solo se oculta en esta vista */ }
    setDismissed(true);
  };

  return (
    <div className="mb-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 space-y-2" data-testid={`card-close-macro-${recipe.id}`}>
      <p className="text-xs font-bold text-amber-200">
        {adminEmail ? `Esta receta quedó baja en ${macroName} para la meta de la clienta.` : `Excelente opción, pero es baja en ${macroName} para cumplir tu meta.`}
      </p>
      <p className="text-[11px] text-amber-100/80">
        Faltan <span className="font-bold">{Math.round(deficit.deficit_g * 10) / 10} g</span> de {macroName}.
      </p>
      {surplus.length > 0 && (
        <p className="text-[11px] text-amber-100/80" data-testid={`card-close-macro-surplus-${recipe.id}`}>
          Ojo: ya sobra {surplus.map(x => `${Math.round(x.surplus_g * 10) / 10} g de ${MACRO_HUMAN[x.macro] || x.macro}`).join(' y ')}; sumar un alimento sube las calorias totales.
        </p>
      )}
      {!open ? (
        <div className="flex gap-2">
          {calOver ? (
            <p className="flex-1 text-[11px] text-amber-100/80" data-testid={`card-close-macro-calover-${recipe.id}`}>
              Esta receta ya supera tus calorias objetivo, por eso no se sugiere agregar mas alimentos.
            </p>
          ) : (
            <button onClick={openPicker}
              className="flex-1 py-1.5 rounded-full text-xs font-bold text-black bg-amber-400 hover:bg-amber-500 transition-all"
              data-testid={`card-close-macro-open-${recipe.id}`}>
              Agregar un alimento
            </button>
          )}
          <button onClick={dismiss}
            className="px-3 py-1.5 rounded-full text-xs text-white/80 border border-white/20"
            data-testid={`card-close-macro-dismiss-${recipe.id}`}>
            Omitir
          </button>
        </div>
      ) : loading ? (
        <p className="text-[11px] text-gray-300">Cargando…</p>
      ) : boost && !pickFood ? (
        <>
          <p className="text-[11px] text-amber-100" data-testid={`card-close-macro-boost-${recipe.id}`}>
            Ya tiene <span className="font-bold">{boost.name}</span>: se sube de <span className="font-bold">{boost.from_g} g</span> a <span className="font-bold">{boost.to_g} g</span> (+{boost.added_g} g) y así cubre la {macroName} sin agregar otro alimento.
            {' '}Quedaría en {Math.round(boost.after?.calories)} kcal.
          </p>
          <div className="flex gap-2">
            <button onClick={submitBoost} disabled={submitting}
              className="flex-1 py-1.5 rounded-full text-xs font-bold text-black bg-amber-400 hover:bg-amber-500 disabled:opacity-50 transition-all"
              data-testid={`card-close-macro-boost-submit-${recipe.id}`}>
              {submitting ? 'Actualizando…' : `Subir ${boost.name} y actualizar totales`}
            </button>
            <button onClick={() => setOpen(false)}
              className="px-3 py-1.5 rounded-full text-xs text-white/80 border border-white/20">
              Cancelar
            </button>
          </div>
          <button onClick={loadFoods} className="text-[11px] underline text-amber-100/80"
            data-testid={`card-close-macro-other-food-${recipe.id}`}>
            Prefiero agregar otro alimento
          </button>
        </>
      ) : (
        <>
          {!boost && BOOST_REASON_TEXT[boostReason] && (
            <p className="text-[11px] text-amber-100" data-testid={`card-close-macro-boost-reason-${recipe.id}`}>
              {BOOST_REASON_TEXT[boostReason](macroName)}
            </p>
          )}
          <select value={chosen} onChange={(e) => setChosen(e.target.value)}
            className="w-full bg-gray-900 border border-amber-400/40 text-white rounded-md px-2 py-1.5 text-xs"
            data-testid={`card-close-macro-select-${recipe.id}`}>
            <option value="">— Seleccionar alimento —</option>
            {options.map(o => (
              <option key={o.name} value={o.name}>
                {o.is_user_food ? '★ ' : ''}{o.name} · {Number(o[deficit.macro] ?? o.per_100g).toFixed(1)} g {macroName}/100g
              </option>
            ))}
          </select>
          <div className="flex gap-2">
            <button onClick={submit} disabled={!chosen || submitting}
              className="flex-1 py-1.5 rounded-full text-xs font-bold text-black bg-amber-400 hover:bg-amber-500 disabled:opacity-50 transition-all"
              data-testid={`card-close-macro-submit-${recipe.id}`}>
              {submitting ? 'Sumando…' : 'Sumar a la receta y actualizar totales'}
            </button>
            <button onClick={() => setOpen(false)}
              className="px-3 py-1.5 rounded-full text-xs text-white/80 border border-white/20">
              Cancelar
            </button>
          </div>
        </>
      )}
    </div>
  );
}
